import { Plugin, PluginKey, TextSelection, Selection, NodeSelection, EditorState } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { DOMSerializer, Schema, DOMParser, Fragment, Slice, Node as Node$1 } from 'prosemirror-model';
import { keymap } from 'prosemirror-keymap';
import { inputRules, undoInputRule as undoInputRule$2, InputRule } from 'prosemirror-inputrules';
import { liftTarget, ReplaceStep, ReplaceAroundStep, canSplit } from 'prosemirror-transform';
import { createParagraphNear as createParagraphNear$2, deleteSelection as deleteSelection$2, exitCode as exitCode$2, joinBackward as joinBackward$2, joinForward as joinForward$2, lift as lift$2, liftEmptyBlock as liftEmptyBlock$2, newlineInCode as newlineInCode$2, selectAll as selectAll$2, selectNodeBackward as selectNodeBackward$2, selectNodeForward as selectNodeForward$2, selectParentNode as selectParentNode$2, setBlockType, wrapIn as wrapIn$2 } from 'prosemirror-commands';
import { liftListItem as liftListItem$2, sinkListItem as sinkListItem$2, wrapInList as wrapInList$2 } from 'prosemirror-schema-list';
import { CellSelection } from 'prosemirror-tables';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __classPrivateFieldGet(receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
}

function magicMethods(Clazz) {
    const classHandler = Object.create(null);
    classHandler.construct = (_, args) => {
        const instance = new Clazz(...args);
        const instanceHandler = Object.create(null);
        const get = Object.getOwnPropertyDescriptor(Clazz.prototype, '__get');
        if (get) {
            instanceHandler.get = (target, name) => {
                if (typeof name !== 'string') {
                    return;
                }
                const exists = name in target
                    || name.startsWith('_')
                    || ['then', 'catch'].includes(name);
                if (exists) {
                    return target[name];
                }
                return get.value.call(target, name);
            };
        }
        instance.proxy = new Proxy(instance, instanceHandler);
        instance.emit('createdProxy');
        return instance.proxy;
    };
    return new Proxy(Clazz, classHandler);
}

function elementFromString(value) {
    const htmlString = `<div>${value}</div>`;
    const parser = new window.DOMParser();
    const element = parser.parseFromString(htmlString, 'text/html').body;
    return element;
}

function getNodeType(nameOrType, schema) {
    if (typeof nameOrType === 'string') {
        return schema.nodes[nameOrType];
    }
    return nameOrType;
}

function getNodeAttributes(state, typeOrName) {
    const type = getNodeType(typeOrName, state.schema);
    const { from, to } = state.selection;
    let nodes = [];
    state.doc.nodesBetween(from, to, node => {
        nodes = [...nodes, node];
    });
    const node = nodes
        .reverse()
        .find(nodeItem => nodeItem.type.name === type.name);
    if (node) {
        return { ...node.attrs };
    }
    return {};
}

function getMarkType(nameOrType, schema) {
    if (typeof nameOrType === 'string') {
        return schema.marks[nameOrType];
    }
    return nameOrType;
}

function getMarkAttributes(state, typeOrName) {
    const type = getMarkType(typeOrName, state.schema);
    const { from, to, empty } = state.selection;
    let marks = [];
    if (empty) {
        marks = state.selection.$head.marks();
    }
    else {
        state.doc.nodesBetween(from, to, node => {
            marks = [...marks, ...node.marks];
        });
    }
    const mark = marks.find(markItem => markItem.type.name === type.name);
    if (mark) {
        return { ...mark.attrs };
    }
    return {};
}

/**
 * Check if object1 includes object2
 * @param object1 Object
 * @param object2 Object
 */
function objectIncludes(object1, object2) {
    const keys = Object.keys(object2);
    if (!keys.length) {
        return true;
    }
    return !!keys
        .filter(key => object2[key] === object1[key])
        .length;
}

function isNodeActive(state, typeOrName, attributes = {}) {
    const { from, to, empty } = state.selection;
    const type = typeOrName
        ? getNodeType(typeOrName, state.schema)
        : null;
    let nodeRanges = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isText) {
            const relativeFrom = Math.max(from, pos);
            const relativeTo = Math.min(to, pos + node.nodeSize);
            nodeRanges = [...nodeRanges, {
                    node,
                    from: relativeFrom,
                    to: relativeTo,
                }];
        }
    });
    if (empty) {
        return !!nodeRanges
            .filter(nodeRange => {
            if (!type) {
                return true;
            }
            return type.name === nodeRange.node.type.name;
        })
            .find(nodeRange => objectIncludes(nodeRange.node.attrs, attributes));
    }
    const selectionRange = to - from;
    const range = nodeRanges
        .filter(nodeRange => {
        if (!type) {
            return true;
        }
        return type.name === nodeRange.node.type.name;
    })
        .filter(nodeRange => objectIncludes(nodeRange.node.attrs, attributes))
        .reduce((sum, nodeRange) => {
        const size = nodeRange.to - nodeRange.from;
        return sum + size;
    }, 0);
    return range >= selectionRange;
}

function isMarkActive(state, typeOrName, attributes = {}) {
    const { from, to, empty } = state.selection;
    const type = typeOrName
        ? getMarkType(typeOrName, state.schema)
        : null;
    if (empty) {
        return !!(state.storedMarks || state.selection.$from.marks())
            .filter(mark => {
            if (!type) {
                return true;
            }
            return type.name === mark.type.name;
        })
            .find(mark => objectIncludes(mark.attrs, attributes));
    }
    let selectionRange = 0;
    let markRanges = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.isText) {
            const relativeFrom = Math.max(from, pos);
            const relativeTo = Math.min(to, pos + node.nodeSize);
            const range = relativeTo - relativeFrom;
            selectionRange += range;
            markRanges = [...markRanges, ...node.marks.map(mark => ({
                    mark,
                    from: relativeFrom,
                    to: relativeTo,
                }))];
        }
    });
    if (selectionRange === 0) {
        return false;
    }
    const range = markRanges
        .filter(markRange => {
        if (!type) {
            return true;
        }
        return type.name === markRange.mark.type.name;
    })
        .filter(markRange => objectIncludes(markRange.mark.attrs, attributes))
        .reduce((sum, markRange) => {
        const size = markRange.to - markRange.from;
        return sum + size;
    }, 0);
    return range >= selectionRange;
}

function getSchemaTypeNameByName(name, schema) {
    if (schema.nodes[name]) {
        return 'node';
    }
    if (schema.marks[name]) {
        return 'mark';
    }
    return null;
}

function isActive(state, name, attributes = {}) {
    if (!name) {
        return isNodeActive(state, null, attributes) || isMarkActive(state, null, attributes);
    }
    const schemaType = getSchemaTypeNameByName(name, state.schema);
    if (schemaType === 'node') {
        return isNodeActive(state, name, attributes);
    }
    if (schemaType === 'mark') {
        return isMarkActive(state, name, attributes);
    }
    return false;
}

function removeElement(element) {
    if (element && element.parentNode) {
        element.parentNode.removeChild(element);
    }
}

function getHTMLFromFragment(doc, schema) {
    const fragment = DOMSerializer
        .fromSchema(schema)
        .serializeFragment(doc.content);
    const temporaryDocument = document.implementation.createHTMLDocument();
    const container = temporaryDocument.createElement('div');
    container.appendChild(fragment);
    return container.innerHTML;
}

function createStyleTag(style) {
    const styleNode = document.createElement('style');
    styleNode.innerHTML = style;
    document.getElementsByTagName('head')[0].appendChild(styleNode);
    return styleNode;
}

function getAllMethodNames(obj) {
    return Reflect.ownKeys(Reflect.getPrototypeOf(obj))
        .map(name => name.toString());
}

class CommandManager {
    constructor(editor) {
        this.commands = {};
        this.methodNames = [];
        this.editor = editor;
        this.methodNames = getAllMethodNames(this.editor);
    }
    /**
     * Register a command.
     *
     * @param name The name of your command
     * @param callback The method of your command
     */
    registerCommand(name, callback) {
        if (this.commands[name]) {
            throw new Error(`tiptap: command '${name}' is already defined.`);
        }
        if (this.methodNames.includes(name)) {
            throw new Error(`tiptap: '${name}' is a protected name.`);
        }
        this.commands[name] = callback;
        return this.editor;
    }
    createCommands() {
        const { commands, editor } = this;
        const { state, view } = editor;
        const { tr } = state;
        const props = this.buildProps(tr);
        return Object.fromEntries(Object
            .entries(commands)
            .map(([name, command]) => {
            const method = (...args) => {
                const callback = command(...args)(props);
                if (!tr.getMeta('preventDispatch')) {
                    view.dispatch(tr);
                }
                return callback;
            };
            return [name, method];
        }));
    }
    createChain(startTr, shouldDispatch = true) {
        const { commands, editor } = this;
        const { state, view } = editor;
        const callbacks = [];
        const hasStartTransaction = !!startTr;
        const tr = startTr || state.tr;
        return new Proxy({}, {
            get: (_, name, proxy) => {
                if (name === 'run') {
                    if (!hasStartTransaction && shouldDispatch && !tr.getMeta('preventDispatch')) {
                        view.dispatch(tr);
                    }
                    return () => callbacks.every(callback => callback === true);
                }
                const command = commands[name];
                if (!command) {
                    throw new Error(`tiptap: command '${name}' not found.`);
                }
                return (...args) => {
                    const props = this.buildProps(tr, shouldDispatch);
                    const callback = command(...args)(props);
                    callbacks.push(callback);
                    return proxy;
                };
            },
        });
    }
    createCan(startTr) {
        const { commands, editor } = this;
        const { state } = editor;
        const dispatch = false;
        const tr = startTr || state.tr;
        const props = this.buildProps(tr, dispatch);
        const formattedCommands = Object.fromEntries(Object
            .entries(commands)
            .map(([name, command]) => {
            return [name, (...args) => command(...args)({ ...props, dispatch })];
        }));
        return {
            ...formattedCommands,
            chain: () => this.createChain(tr, dispatch),
        };
    }
    buildProps(tr, shouldDispatch = true) {
        const { editor, commands } = this;
        const { state, view } = editor;
        if (state.storedMarks) {
            tr.setStoredMarks(state.storedMarks);
        }
        const props = {
            tr,
            editor,
            view,
            state: this.chainableState(tr, state),
            dispatch: shouldDispatch
                ? () => undefined
                : undefined,
            chain: () => this.createChain(tr),
            can: () => this.createCan(tr),
            get commands() {
                return Object.fromEntries(Object
                    .entries(commands)
                    .map(([name, command]) => {
                    return [name, (...args) => command(...args)(props)];
                }));
            },
        };
        return props;
    }
    chainableState(tr, state) {
        let { selection } = tr;
        let { doc } = tr;
        let { storedMarks } = tr;
        return {
            ...state,
            schema: state.schema,
            plugins: state.plugins,
            apply: state.apply.bind(state),
            applyTransaction: state.applyTransaction.bind(state),
            reconfigure: state.reconfigure.bind(state),
            toJSON: state.toJSON.bind(state),
            get storedMarks() {
                return storedMarks;
            },
            get selection() {
                return selection;
            },
            get doc() {
                return doc;
            },
            get tr() {
                selection = tr.selection;
                doc = tr.doc;
                storedMarks = tr.storedMarks;
                return tr;
            },
        };
    }
}

function splitExtensions(extensions) {
    const baseExtensions = extensions.filter(extension => extension.type === 'extension');
    const nodeExtensions = extensions.filter(extension => extension.type === 'node');
    const markExtensions = extensions.filter(extension => extension.type === 'mark');
    return {
        baseExtensions,
        nodeExtensions,
        markExtensions,
    };
}

/**
 * Get a list of all extension attributes defined in `addAttribute` and `addGlobalAttribute`.
 * @param extensions List of extensions
 */
function getAttributesFromExtensions(extensions) {
    const extensionAttributes = [];
    const { nodeExtensions, markExtensions } = splitExtensions(extensions);
    const nodeAndMarkExtensions = [...nodeExtensions, ...markExtensions];
    const defaultAttribute = {
        default: null,
        rendered: true,
        renderHTML: null,
        parseHTML: null,
        keepOnSplit: true,
    };
    extensions.forEach(extension => {
        const context = {
            options: extension.options,
        };
        const globalAttributes = extension.config.addGlobalAttributes.bind(context)();
        globalAttributes.forEach(globalAttribute => {
            globalAttribute.types.forEach(type => {
                Object
                    .entries(globalAttribute.attributes)
                    .forEach(([name, attribute]) => {
                    extensionAttributes.push({
                        type,
                        name,
                        attribute: {
                            ...defaultAttribute,
                            ...attribute,
                        },
                    });
                });
            });
        });
    });
    nodeAndMarkExtensions.forEach(extension => {
        const context = {
            options: extension.options,
        };
        const attributes = extension.config.addAttributes.bind(context)();
        Object
            .entries(attributes)
            .forEach(([name, attribute]) => {
            extensionAttributes.push({
                type: extension.config.name,
                name,
                attribute: {
                    ...defaultAttribute,
                    ...attribute,
                },
            });
        });
    });
    return extensionAttributes;
}

function mergeAttributes(...objects) {
    return objects
        .filter(item => !!item)
        .reduce((items, item) => {
        const mergedAttributes = { ...items };
        Object.entries(item).forEach(([key, value]) => {
            const exists = mergedAttributes[key];
            if (!exists) {
                mergedAttributes[key] = value;
                return;
            }
            if (key === 'class') {
                mergedAttributes[key] = [mergedAttributes[key], value].join(' ');
            }
            else if (key === 'style') {
                mergedAttributes[key] = [mergedAttributes[key], value].join('; ');
            }
            else {
                mergedAttributes[key] = value;
            }
        });
        return mergedAttributes;
    }, {});
}

function getRenderedAttributes(nodeOrMark, extensionAttributes) {
    return extensionAttributes
        .filter(item => item.attribute.rendered)
        .map(item => {
        if (!item.attribute.renderHTML) {
            return {
                [item.name]: nodeOrMark.attrs[item.name],
            };
        }
        return item.attribute.renderHTML(nodeOrMark.attrs) || {};
    })
        .reduce((attributes, attribute) => {
        return mergeAttributes(attributes, attribute);
    }, {});
}

function isEmptyObject(object = {}) {
    return Object.keys(object).length === 0 && object.constructor === Object;
}

function fromString(value) {
    if (typeof value !== 'string') {
        return value;
    }
    if (value.match(/^\d*(\.\d+)?$/)) {
        return Number(value);
    }
    if (value === 'true') {
        return true;
    }
    if (value === 'false') {
        return false;
    }
    return value;
}

/**
 * This function merges extension attributes into parserule attributes (`attrs` or `getAttrs`).
 * Cancels when `getAttrs` returned `false`.
 * @param parseRule ProseMirror ParseRule
 * @param extensionAttributes List of attributes to inject
 */
function injectExtensionAttributesToParseRule(parseRule, extensionAttributes) {
    if (parseRule.style) {
        return parseRule;
    }
    return {
        ...parseRule,
        getAttrs: node => {
            const oldAttributes = parseRule.getAttrs
                ? parseRule.getAttrs(node)
                : parseRule.attrs;
            if (oldAttributes === false) {
                return false;
            }
            const newAttributes = extensionAttributes
                .filter(item => item.attribute.rendered)
                .reduce((items, item) => {
                const attributes = item.attribute.parseHTML
                    ? item.attribute.parseHTML(node) || {}
                    : {
                        [item.name]: fromString(node.getAttribute(item.name)),
                    };
                const filteredAttributes = Object.fromEntries(Object.entries(attributes)
                    .filter(([, value]) => value !== undefined && value !== null));
                return {
                    ...items,
                    ...filteredAttributes,
                };
            }, {});
            return { ...oldAttributes, ...newAttributes };
        },
    };
}

/**
 * Optionally calls `value` as a function.
 * Otherwise it is returned directly.
 * @param value Function or any value.
 * @param context Optional context to bind to function.
 */
function callOrReturn(value, context) {
    if (typeof value === 'function') {
        if (context) {
            return value.bind(context)();
        }
        return value();
    }
    return value;
}

function cleanUpSchemaItem(data) {
    return Object.fromEntries(Object.entries(data).filter(([key, value]) => {
        if (key === 'attrs' && isEmptyObject(value)) {
            return false;
        }
        return value !== null && value !== undefined;
    }));
}
function getSchema(extensions) {
    var _a;
    const allAttributes = getAttributesFromExtensions(extensions);
    const { nodeExtensions, markExtensions } = splitExtensions(extensions);
    const topNode = (_a = nodeExtensions.find(extension => extension.config.topNode)) === null || _a === void 0 ? void 0 : _a.config.name;
    const nodes = Object.fromEntries(nodeExtensions.map(extension => {
        var _a;
        const extensionAttributes = allAttributes.filter(attribute => attribute.type === extension.config.name);
        const context = { options: extension.options };
        const schema = cleanUpSchemaItem({
            content: callOrReturn(extension.config.content, context),
            marks: callOrReturn(extension.config.marks, context),
            group: callOrReturn(extension.config.group, context),
            inline: callOrReturn(extension.config.inline, context),
            atom: callOrReturn(extension.config.atom, context),
            selectable: callOrReturn(extension.config.selectable, context),
            draggable: callOrReturn(extension.config.draggable, context),
            code: callOrReturn(extension.config.code, context),
            defining: callOrReturn(extension.config.defining, context),
            isolating: callOrReturn(extension.config.isolating, context),
            tableRole: callOrReturn(extension.config.tableRole, context),
            attrs: Object.fromEntries(extensionAttributes.map(extensionAttribute => {
                var _a;
                return [extensionAttribute.name, { default: (_a = extensionAttribute === null || extensionAttribute === void 0 ? void 0 : extensionAttribute.attribute) === null || _a === void 0 ? void 0 : _a.default }];
            })),
        });
        if (extension.config.parseHTML) {
            schema.parseDOM = (_a = extension.config.parseHTML
                .bind(context)()) === null || _a === void 0 ? void 0 : _a.map(parseRule => injectExtensionAttributesToParseRule(parseRule, extensionAttributes));
        }
        if (extension.config.renderHTML) {
            schema.toDOM = node => { var _a; return (_a = extension.config.renderHTML) === null || _a === void 0 ? void 0 : _a.bind(context)({
                node,
                HTMLAttributes: getRenderedAttributes(node, extensionAttributes),
            }); };
        }
        return [extension.config.name, schema];
    }));
    const marks = Object.fromEntries(markExtensions.map(extension => {
        var _a;
        const extensionAttributes = allAttributes.filter(attribute => attribute.type === extension.config.name);
        const context = { options: extension.options };
        const schema = cleanUpSchemaItem({
            inclusive: callOrReturn(extension.config.inclusive, context),
            excludes: callOrReturn(extension.config.excludes, context),
            group: callOrReturn(extension.config.group, context),
            spanning: callOrReturn(extension.config.spanning, context),
            attrs: Object.fromEntries(extensionAttributes.map(extensionAttribute => {
                var _a;
                return [extensionAttribute.name, { default: (_a = extensionAttribute === null || extensionAttribute === void 0 ? void 0 : extensionAttribute.attribute) === null || _a === void 0 ? void 0 : _a.default }];
            })),
        });
        if (extension.config.parseHTML) {
            schema.parseDOM = (_a = extension.config.parseHTML
                .bind(context)()) === null || _a === void 0 ? void 0 : _a.map(parseRule => injectExtensionAttributesToParseRule(parseRule, extensionAttributes));
        }
        if (extension.config.renderHTML) {
            schema.toDOM = mark => { var _a; return (_a = extension.config.renderHTML) === null || _a === void 0 ? void 0 : _a.bind(context)({
                mark,
                HTMLAttributes: getRenderedAttributes(mark, extensionAttributes),
            }); };
        }
        return [extension.config.name, schema];
    }));
    return new Schema({
        topNode,
        nodes,
        marks,
    });
}

function getSchemaTypeByName(name, schema) {
    if (schema.nodes[name]) {
        return schema.nodes[name];
    }
    if (schema.marks[name]) {
        return schema.marks[name];
    }
    return null;
}

class ExtensionManager {
    constructor(extensions, editor) {
        this.editor = editor;
        this.extensions = extensions;
        this.schema = getSchema(this.extensions);
        this.extensions.forEach(extension => {
            const context = {
                options: extension.options,
                editor: this.editor,
                type: getSchemaTypeByName(extension.config.name, this.schema),
            };
            const commands = extension.config.addCommands.bind(context)();
            editor.registerCommands(commands);
            if (typeof extension.config.onCreate === 'function') {
                this.editor.on('create', extension.config.onCreate.bind(context));
            }
            if (typeof extension.config.onUpdate === 'function') {
                this.editor.on('update', extension.config.onUpdate.bind(context));
            }
            if (typeof extension.config.onSelection === 'function') {
                this.editor.on('selection', extension.config.onSelection.bind(context));
            }
            if (typeof extension.config.onTransaction === 'function') {
                this.editor.on('transaction', extension.config.onTransaction.bind(context));
            }
            if (typeof extension.config.onFocus === 'function') {
                this.editor.on('focus', extension.config.onFocus.bind(context));
            }
            if (typeof extension.config.onBlur === 'function') {
                this.editor.on('blur', extension.config.onBlur.bind(context));
            }
            if (typeof extension.config.onDestroy === 'function') {
                this.editor.on('destroy', extension.config.onDestroy.bind(context));
            }
        });
    }
    get plugins() {
        return [...this.extensions]
            .reverse()
            .map(extension => {
            const context = {
                options: extension.options,
                editor: this.editor,
                type: getSchemaTypeByName(extension.config.name, this.schema),
            };
            const keymapPlugin = keymap(extension.config.addKeyboardShortcuts.bind(context)());
            const inputRules$1 = extension.config.addInputRules.bind(context)();
            const inputRulePlugins = this.editor.options.enableInputRules && inputRules$1.length
                ? [inputRules({ rules: inputRules$1 })]
                : [];
            const pasteRulePlugins = this.editor.options.enablePasteRules
                ? extension.config.addPasteRules.bind(context)()
                : [];
            const plugins = extension.config.addProseMirrorPlugins.bind(context)();
            return [
                keymapPlugin,
                ...inputRulePlugins,
                ...pasteRulePlugins,
                ...plugins,
            ];
        })
            .flat();
    }
    get attributes() {
        return getAttributesFromExtensions(this.extensions);
    }
    get nodeViews() {
        const { editor } = this;
        const { nodeExtensions } = splitExtensions(this.extensions);
        return Object.fromEntries(nodeExtensions
            .filter(extension => !!extension.config.addNodeView)
            .map(extension => {
            var _a;
            const extensionAttributes = this.attributes.filter(attribute => attribute.type === extension.config.name);
            const context = {
                options: extension.options,
                editor,
                type: getNodeType(extension.config.name, this.schema),
            };
            const renderer = (_a = extension.config.addNodeView) === null || _a === void 0 ? void 0 : _a.call(context);
            const nodeview = (node, view, getPos, decorations) => {
                const HTMLAttributes = getRenderedAttributes(node, extensionAttributes);
                return renderer({
                    editor,
                    node,
                    getPos,
                    decorations,
                    HTMLAttributes,
                    extension,
                });
            };
            return [extension.config.name, nodeview];
        }));
    }
    get textSerializers() {
        const { editor } = this;
        const { nodeExtensions } = splitExtensions(this.extensions);
        return Object.fromEntries(nodeExtensions
            .filter(extension => !!extension.config.renderText)
            .map(extension => {
            const context = {
                options: extension.options,
                editor,
                type: getNodeType(extension.config.name, this.schema),
            };
            const textSerializer = (props) => { var _a; return (_a = extension.config.renderText) === null || _a === void 0 ? void 0 : _a.call(context, props); };
            return [extension.config.name, textSerializer];
        }));
    }
}

class EventEmitter {
    constructor() {
        this.callbacks = {};
    }
    on(event, fn) {
        if (!this.callbacks[event]) {
            this.callbacks[event] = [];
        }
        this.callbacks[event].push(fn);
        return this;
    }
    emit(event, ...args) {
        const callbacks = this.callbacks[event];
        if (callbacks) {
            callbacks.forEach(callback => callback.apply(this, args));
        }
        return this;
    }
    off(event, fn) {
        const callbacks = this.callbacks[event];
        if (callbacks) {
            if (fn) {
                this.callbacks[event] = callbacks.filter(callback => callback !== fn);
            }
            else {
                delete this.callbacks[event];
            }
        }
        return this;
    }
    removeAllListeners() {
        this.callbacks = {};
    }
}

// see: https://github.com/mesqueeb/is-what/blob/88d6e4ca92fb2baab6003c54e02eedf4e729e5ab/src/index.ts
function getType(payload) {
    return Object.prototype.toString.call(payload).slice(8, -1);
}
function isPlainObject(payload) {
    if (getType(payload) !== 'Object')
        return false;
    return payload.constructor === Object && Object.getPrototypeOf(payload) === Object.prototype;
}

function mergeDeep(target, source) {
    const output = { ...target };
    if (isPlainObject(target) && isPlainObject(source)) {
        Object.keys(source).forEach(key => {
            if (isPlainObject(source[key])) {
                if (!(key in target)) {
                    Object.assign(output, { [key]: source[key] });
                }
                else {
                    output[key] = mergeDeep(target[key], source[key]);
                }
            }
            else {
                Object.assign(output, { [key]: source[key] });
            }
        });
    }
    return output;
}

var _configure;
class Extension {
    constructor(config) {
        this.type = 'extension';
        this.config = {
            name: 'extension',
            defaultOptions: {},
            addGlobalAttributes: () => [],
            addCommands: () => ({}),
            addKeyboardShortcuts: () => ({}),
            addInputRules: () => [],
            addPasteRules: () => [],
            addProseMirrorPlugins: () => [],
            onCreate: null,
            onUpdate: null,
            onSelection: null,
            onTransaction: null,
            onFocus: null,
            onBlur: null,
            onDestroy: null,
        };
        _configure.set(this, (options) => {
            this.options = mergeDeep(this.config.defaultOptions, options);
            return this;
        });
        this.config = {
            ...this.config,
            ...config,
        };
        this.options = this.config.defaultOptions;
    }
    static create(config) {
        return new Extension(config);
    }
    configure(options = {}) {
        var _a;
        return __classPrivateFieldGet((_a = Extension
            .create(this.config)), _configure).call(_a, options);
    }
    extend(extendedConfig) {
        return new Extension({
            ...this.config,
            ...extendedConfig,
        });
    }
}
_configure = new WeakMap();

const textBetween = (editor, from, to, blockSeparator, leafText) => {
    let text = '';
    let separated = true;
    editor.state.doc.nodesBetween(from, to, (node, pos) => {
        var _a;
        const textSerializer = editor.extensionManager.textSerializers[node.type.name];
        if (textSerializer) {
            text += textSerializer({ node });
            separated = !blockSeparator;
        }
        else if (node.isText) {
            text += (_a = node === null || node === void 0 ? void 0 : node.text) === null || _a === void 0 ? void 0 : _a.slice(Math.max(from, pos) - pos, to - pos);
            separated = !blockSeparator;
        }
        else if (node.isLeaf && leafText) {
            text += leafText;
            separated = !blockSeparator;
        }
        else if (!separated && node.isBlock) {
            text += blockSeparator;
            separated = true;
        }
    }, 0);
    return text;
};
const ClipboardTextSerializer = Extension.create({
    name: 'editable',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('clipboardTextSerializer'),
                props: {
                    clipboardTextSerializer: () => {
                        const { editor } = this;
                        const { from, to } = editor.state.selection;
                        return textBetween(editor, from, to, '\n');
                    },
                },
            }),
        ];
    },
});

/**
 * Removes focus from the editor.
 */
const blur = () => ({ view }) => {
    const element = view.dom;
    element.blur();
    return true;
};

var blur$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    blur: blur
});

/**
 * Clear the whole document.
 */
const clearContent = (emitUpdate = false) => ({ commands }) => {
    return commands.setContent('', emitUpdate);
};

var clearContent$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    clearContent: clearContent
});

/**
 * Normalize nodes to a simple paragraph.
 */
const clearNodes = () => ({ state, tr, dispatch }) => {
    const { selection } = tr;
    const { from, to } = selection;
    state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.type.isText) {
            const fromPos = tr.doc.resolve(tr.mapping.map(pos + 1));
            const toPos = tr.doc.resolve(tr.mapping.map(pos + node.nodeSize - 1));
            const nodeRange = fromPos.blockRange(toPos);
            if (nodeRange) {
                const targetLiftDepth = liftTarget(nodeRange);
                if (node.type.isTextblock && dispatch) {
                    tr.setNodeMarkup(nodeRange.start, state.doc.type.contentMatch.defaultType);
                }
                if ((targetLiftDepth || targetLiftDepth === 0) && dispatch) {
                    tr.lift(nodeRange, targetLiftDepth);
                }
            }
        }
    });
    return true;
};

var clearNodes$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    clearNodes: clearNodes
});

/**
 * Define a command inline.
 */
const command = (fn) => props => {
    return fn(props);
};

var command$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    command: command
});

/**
 * Create a paragraph nearby.
 */
const createParagraphNear = () => ({ state, dispatch }) => {
    return createParagraphNear$2(state, dispatch);
};

var createParagraphNear$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    createParagraphNear: createParagraphNear
});

/**
 * Delete a given range.
 */
const deleteRange = (range) => ({ tr, dispatch }) => {
    const { from, to } = range;
    if (dispatch) {
        tr.delete(from, to);
    }
    return true;
};

var deleteRange$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    deleteRange: deleteRange
});

/**
 * Delete the selection, if there is one.
 */
const deleteSelection = () => ({ state, dispatch }) => {
    return deleteSelection$2(state, dispatch);
};

var deleteSelection$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    deleteSelection: deleteSelection
});

/**
 * Exit from a code block.
 */
const exitCode = () => ({ state, dispatch }) => {
    return exitCode$2(state, dispatch);
};

var exitCode$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    exitCode: exitCode
});

function getMarkRange($pos, type) {
    if (!$pos || !type) {
        return;
    }
    const start = $pos.parent.childAfter($pos.parentOffset);
    if (!start.node) {
        return;
    }
    const link = start.node.marks.find(mark => mark.type === type);
    if (!link) {
        return;
    }
    let startIndex = $pos.index();
    let startPos = $pos.start() + start.offset;
    let endIndex = startIndex + 1;
    let endPos = startPos + start.node.nodeSize;
    while (startIndex > 0 && link.isInSet($pos.parent.child(startIndex - 1).marks)) {
        startIndex -= 1;
        startPos -= $pos.parent.child(startIndex).nodeSize;
    }
    while (endIndex < $pos.parent.childCount && link.isInSet($pos.parent.child(endIndex).marks)) {
        endPos += $pos.parent.child(endIndex).nodeSize;
        endIndex += 1;
    }
    return {
        from: startPos,
        to: endPos,
    };
}

/**
 * Extends the text selection to the current mark.
 */
const extendMarkRange = (typeOrName) => ({ tr, state, dispatch }) => {
    const type = getMarkType(typeOrName, state.schema);
    const { doc, selection } = tr;
    const { $from, empty } = selection;
    if (empty && dispatch) {
        const range = getMarkRange($from, type);
        if (range) {
            const newSelection = TextSelection.create(doc, range.from, range.to);
            tr.setSelection(newSelection);
        }
    }
    return true;
};

var extendMarkRange$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    extendMarkRange: extendMarkRange
});

/**
 * Runs one command after the other and stops at the first which returns true.
 */
const first = (commands) => props => {
    const items = typeof commands === 'function'
        ? commands(props)
        : commands;
    for (let i = 0; i < items.length; i += 1) {
        if (items[i](props)) {
            return true;
        }
    }
    return false;
};

var first$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    first: first
});

function minMax(value = 0, min = 0, max = 0) {
    return Math.min(Math.max(value, min), max);
}

function isClass(item) {
    var _a;
    if (((_a = item.constructor) === null || _a === void 0 ? void 0 : _a.toString().substring(0, 5)) !== 'class') {
        return false;
    }
    return true;
}

function isObject(item) {
    return (item
        && typeof item === 'object'
        && !Array.isArray(item)
        && !isClass(item));
}

function isTextSelection(value) {
    return isObject(value) && value instanceof TextSelection;
}

function resolveSelection(state, position = null) {
    if (!position) {
        return null;
    }
    if (position === 'start' || position === true) {
        return {
            from: 0,
            to: 0,
        };
    }
    if (position === 'end') {
        const { size } = state.doc.content;
        return {
            from: size,
            to: size,
        };
    }
    return {
        from: position,
        to: position,
    };
}
/**
 * Focus the editor at the given position.
 */
const focus = (position = null) => ({ editor, view, tr, dispatch, }) => {
    if ((view.hasFocus() && position === null) || position === false) {
        return true;
    }
    // we don’t try to resolve a NodeSelection or CellSelection
    if (dispatch && position === null && !isTextSelection(editor.state.selection)) {
        view.focus();
        return true;
    }
    const { from, to } = resolveSelection(editor.state, position) || editor.state.selection;
    const { doc } = tr;
    const resolvedFrom = minMax(from, 0, doc.content.size);
    const resolvedEnd = minMax(to, 0, doc.content.size);
    const selection = TextSelection.create(doc, resolvedFrom, resolvedEnd);
    if (dispatch) {
        tr.setSelection(selection);
        view.focus();
    }
    return true;
};

var focus$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    focus: focus
});

// TODO: move to utils
// https://github.com/ProseMirror/prosemirror-state/blob/master/src/selection.js#L466
function selectionToInsertionEnd(tr, startLen, bias) {
    const last = tr.steps.length - 1;
    if (last < startLen)
        return;
    const step = tr.steps[last];
    if (!(step instanceof ReplaceStep || step instanceof ReplaceAroundStep))
        return;
    const map = tr.mapping.maps[last];
    let end = 0;
    map.forEach((_from, _to, _newFrom, newTo) => { if (end === 0)
        end = newTo; });
    tr.setSelection(Selection.near(tr.doc.resolve(end), bias));
}
/**
 * Insert a string of HTML at the current position.
 */
const insertHTML = (value) => ({ tr, state, dispatch }) => {
    const { selection } = tr;
    const element = elementFromString(value);
    const slice = DOMParser.fromSchema(state.schema).parseSlice(element);
    if (dispatch) {
        tr.insert(selection.anchor, slice.content);
        selectionToInsertionEnd(tr, tr.steps.length - 1, -1);
    }
    return true;
};

var insertHTML$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    insertHTML: insertHTML
});

/**
 * Insert a string of text at the current position.
 */
const insertText = (value) => ({ tr, dispatch }) => {
    if (dispatch) {
        tr.insertText(value);
    }
    return true;
};

var insertText$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    insertText: insertText
});

/**
 * Join two nodes backward.
 */
const joinBackward = () => ({ state, dispatch }) => {
    return joinBackward$2(state, dispatch);
};

var joinBackward$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    joinBackward: joinBackward
});

/**
 * Join two nodes forward.
 */
const joinForward = () => ({ state, dispatch }) => {
    return joinForward$2(state, dispatch);
};

var joinForward$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    joinForward: joinForward
});

/**
 * Removes an existing wrap.
 */
const lift = (typeOrName, attributes = {}) => ({ state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    const isActive = isNodeActive(state, type, attributes);
    if (!isActive) {
        return false;
    }
    return lift$2(state, dispatch);
};

var lift$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    lift: lift
});

/**
 * Lift block if empty.
 */
const liftEmptyBlock = () => ({ state, dispatch }) => {
    return liftEmptyBlock$2(state, dispatch);
};

var liftEmptyBlock$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    liftEmptyBlock: liftEmptyBlock
});

/**
 * Lift the list item into a wrapping list.
 */
const liftListItem = (typeOrName) => ({ state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    return liftListItem$2(type)(state, dispatch);
};

var liftListItem$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    liftListItem: liftListItem
});

/**
 * Add a newline character in code.
 */
const newlineInCode = () => ({ state, dispatch }) => {
    return newlineInCode$2(state, dispatch);
};

var newlineInCode$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    newlineInCode: newlineInCode
});

/**
 * Replaces text with a node.
 */
const replace = (typeOrName, attributes = {}) => ({ state, commands }) => {
    const { from, to } = state.selection;
    const range = { from, to };
    return commands.replaceRange(range, typeOrName, attributes);
};

var replace$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    replace: replace
});

/**
 * Replaces text with a node within a range.
 */
const replaceRange = (range, typeOrName, attributes = {}) => ({ tr, state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    const { from, to } = range;
    const $from = tr.doc.resolve(from);
    const index = $from.index();
    if (!$from.parent.canReplaceWith(index, index, type)) {
        return false;
    }
    if (dispatch) {
        tr.replaceWith(from, to, type.create(attributes));
    }
    return true;
};

var replaceRange$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    replaceRange: replaceRange
});

/**
 * Remove a property or an array of properties from an object
 * @param obj Object
 * @param key Key to remove
 */
function deleteProps(obj, propOrProps) {
    const props = typeof propOrProps === 'string'
        ? [propOrProps]
        : propOrProps;
    return Object
        .keys(obj)
        .reduce((newObj, prop) => {
        if (!props.includes(prop)) {
            newObj[prop] = obj[prop];
        }
        return newObj;
    }, {});
}

/**
 * Resets node attributes to the default value.
 */
const resetNodeAttributes = (typeOrName, attributes) => ({ tr, state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    const { selection } = tr;
    const { from, to } = selection;
    state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type === type && dispatch) {
            tr.setNodeMarkup(pos, undefined, deleteProps(node.attrs, attributes));
        }
    });
    return true;
};

var resetNodeAttributes$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    resetNodeAttributes: resetNodeAttributes
});

/**
 * Scroll the selection into view.
 */
const scrollIntoView = () => ({ tr, dispatch }) => {
    if (dispatch) {
        tr.scrollIntoView();
    }
    return true;
};

var scrollIntoView$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    scrollIntoView: scrollIntoView
});

/**
 * Select the whole document.
 */
const selectAll = () => ({ state, dispatch }) => {
    return selectAll$2(state, dispatch);
};

var selectAll$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    selectAll: selectAll
});

/**
 * Select a node backward.
 */
const selectNodeBackward = () => ({ state, dispatch }) => {
    return selectNodeBackward$2(state, dispatch);
};

var selectNodeBackward$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    selectNodeBackward: selectNodeBackward
});

/**
 * Select a node forward.
 */
const selectNodeForward = () => ({ state, dispatch }) => {
    return selectNodeForward$2(state, dispatch);
};

var selectNodeForward$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    selectNodeForward: selectNodeForward
});

/**
 * Select the parent node.
 */
const selectParentNode = () => ({ state, dispatch }) => {
    return selectParentNode$2(state, dispatch);
};

var selectParentNode$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    selectParentNode: selectParentNode
});

/**
 * Replace the whole document with new content.
 */
const setContent = (content, emitUpdate = false, parseOptions = {}) => ({ tr, editor, dispatch }) => {
    const { createDocument } = editor;
    const { doc } = tr;
    const document = createDocument(content, parseOptions);
    const selection = TextSelection.create(doc, 0, doc.content.size);
    if (dispatch) {
        tr.setSelection(selection)
            .replaceSelectionWith(document, false)
            .setMeta('preventUpdate', !emitUpdate);
    }
    return true;
};

var setContent$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    setContent: setContent
});

/**
 * Add a mark with new attributes.
 */
const setMark = (typeOrName, attributes) => ({ tr, state, dispatch }) => {
    const { selection } = tr;
    const { from, to, empty } = selection;
    const type = getMarkType(typeOrName, state.schema);
    const oldAttributes = getMarkAttributes(state, type);
    const newAttributes = {
        ...oldAttributes,
        ...attributes,
    };
    if (dispatch) {
        if (empty) {
            tr.addStoredMark(type.create(newAttributes));
        }
        else {
            tr.addMark(from, to, type.create(newAttributes));
        }
    }
    return true;
};

var setMark$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    setMark: setMark
});

/**
 * Replace a given range with a node.
 */
const setNode = (typeOrName, attrs = {}) => ({ state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    return setBlockType(type, attrs)(state, dispatch);
};

var setNode$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    setNode: setNode
});

/**
 * Sink the list item down into an inner list.
 */
const sinkListItem = (typeOrName) => ({ state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    return sinkListItem$2(type)(state, dispatch);
};

var sinkListItem$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    sinkListItem: sinkListItem
});

function getSplittedAttributes(extensionAttributes, typeName, attributes) {
    return Object.fromEntries(Object
        .entries(attributes)
        .filter(([name]) => {
        const extensionAttribute = extensionAttributes.find(item => {
            return item.type === typeName && item.name === name;
        });
        if (!extensionAttribute) {
            return false;
        }
        return extensionAttribute.attribute.keepOnSplit;
    }));
}

function defaultBlockAt(match) {
    for (let i = 0; i < match.edgeCount; i + 1) {
        const { type } = match.edge(i);
        if (type.isTextblock && !type.hasRequiredAttrs()) {
            return type;
        }
    }
    return null;
}
function keepMarks(state) {
    const marks = state.storedMarks
        || (state.selection.$to.parentOffset && state.selection.$from.marks());
    if (marks) {
        state.tr.ensureMarks(marks);
    }
}
/**
 * Forks a new node from an existing node.
 */
const splitBlock = (options = {}) => ({ tr, state, dispatch, editor, }) => {
    const defaultOptions = {
        keepMarks: true,
    };
    const config = { ...defaultOptions, ...options };
    const { selection, doc } = tr;
    const { $from, $to } = selection;
    const extensionAttributes = editor.extensionManager.attributes;
    const newAttributes = getSplittedAttributes(extensionAttributes, $from.node().type.name, $from.node().attrs);
    if (selection instanceof NodeSelection && selection.node.isBlock) {
        if (!$from.parentOffset || !canSplit(doc, $from.pos)) {
            return false;
        }
        if (dispatch) {
            if (config.keepMarks) {
                keepMarks(state);
            }
            tr.split($from.pos).scrollIntoView();
        }
        return true;
    }
    if (!$from.parent.isBlock) {
        return false;
    }
    if (dispatch) {
        const atEnd = $to.parentOffset === $to.parent.content.size;
        if (selection instanceof TextSelection) {
            tr.deleteSelection();
        }
        const deflt = $from.depth === 0
            ? undefined
            : defaultBlockAt($from.node(-1).contentMatchAt($from.indexAfter(-1)));
        let types = atEnd && deflt
            ? [{
                    type: deflt,
                    attrs: newAttributes,
                }]
            : undefined;
        let can = canSplit(tr.doc, tr.mapping.map($from.pos), 1, types);
        if (!types
            && !can
            && canSplit(tr.doc, tr.mapping.map($from.pos), 1, deflt ? [{ type: deflt }] : undefined)) {
            can = true;
            types = deflt
                ? [{
                        type: deflt,
                        attrs: newAttributes,
                    }]
                : undefined;
        }
        if (can) {
            tr.split(tr.mapping.map($from.pos), 1, types);
            if (!atEnd
                && !$from.parentOffset
                && $from.parent.type !== deflt
                && $from.node(-1).canReplace($from.index(-1), $from.indexAfter(-1), Fragment.from(deflt === null || deflt === void 0 ? void 0 : deflt.create()))) {
                tr.setNodeMarkup(tr.mapping.map($from.before()), deflt || undefined);
            }
        }
        if (config.keepMarks) {
            keepMarks(state);
        }
        tr.scrollIntoView();
    }
    return true;
};

var splitBlock$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    splitBlock: splitBlock
});

/**
 * Splits one list item into two list items.
 */
const splitListItem = (typeOrName) => ({ tr, state, dispatch, editor, }) => {
    var _a;
    const type = getNodeType(typeOrName, state.schema);
    const { $from, $to } = state.selection;
    // @ts-ignore
    // eslint-disable-next-line
    const node = state.selection.node;
    if ((node && node.isBlock) || $from.depth < 2 || !$from.sameParent($to)) {
        return false;
    }
    const grandParent = $from.node(-1);
    if (grandParent.type !== type) {
        return false;
    }
    const extensionAttributes = editor.extensionManager.attributes;
    if ($from.parent.content.size === 0 && $from.node(-1).childCount === $from.indexAfter(-1)) {
        // In an empty block. If this is a nested list, the wrapping
        // list item should be split. Otherwise, bail out and let next
        // command handle lifting.
        if ($from.depth === 2
            || $from.node(-3).type !== type
            || $from.index(-2) !== $from.node(-2).childCount - 1) {
            return false;
        }
        if (dispatch) {
            let wrap = Fragment.empty;
            const keepItem = $from.index(-1) > 0;
            // Build a fragment containing empty versions of the structure
            // from the outer list item to the parent node of the cursor
            for (let d = $from.depth - (keepItem ? 1 : 2); d >= $from.depth - 3; d -= 1) {
                wrap = Fragment.from($from.node(d).copy(wrap));
            }
            // Add a second list item with an empty default start node
            const newNextTypeAttributes = getSplittedAttributes(extensionAttributes, $from.node().type.name, $from.node().attrs);
            const nextType = ((_a = type.contentMatch.defaultType) === null || _a === void 0 ? void 0 : _a.createAndFill(newNextTypeAttributes)) || undefined;
            wrap = wrap.append(Fragment.from(type.createAndFill(null, nextType) || undefined));
            tr
                .replace($from.before(keepItem ? undefined : -1), $from.after(-3), new Slice(wrap, keepItem ? 3 : 2, 2))
                .setSelection(TextSelection.near(tr.doc.resolve($from.pos + (keepItem ? 3 : 2))))
                .scrollIntoView();
        }
        return true;
    }
    const nextType = $to.pos === $from.end()
        ? grandParent.contentMatchAt(0).defaultType
        : null;
    const newTypeAttributes = getSplittedAttributes(extensionAttributes, grandParent.type.name, grandParent.attrs);
    const newNextTypeAttributes = getSplittedAttributes(extensionAttributes, $from.node().type.name, $from.node().attrs);
    tr.delete($from.pos, $to.pos);
    const types = nextType
        ? [{ type, attrs: newTypeAttributes }, { type: nextType, attrs: newNextTypeAttributes }]
        : [{ type, attrs: newTypeAttributes }];
    if (!canSplit(tr.doc, $from.pos, 2)) {
        return false;
    }
    if (dispatch) {
        tr.split($from.pos, 2, types).scrollIntoView();
    }
    return true;
};

var splitListItem$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    splitListItem: splitListItem
});

function findParentNodeClosestToPos($pos, predicate) {
    for (let i = $pos.depth; i > 0; i -= 1) {
        const node = $pos.node(i);
        if (predicate(node)) {
            return {
                pos: i > 0 ? $pos.before(i) : 0,
                start: $pos.start(i),
                depth: i,
                node,
            };
        }
    }
}

function findParentNode(predicate) {
    return (selection) => findParentNodeClosestToPos(selection.$from, predicate);
}

function isList(name, extensions) {
    const { nodeExtensions } = splitExtensions(extensions);
    const extension = nodeExtensions.find(item => item.config.name === name);
    if (!extension) {
        return false;
    }
    const groups = callOrReturn(extension.config.group, { options: extension.options });
    if (typeof groups !== 'string') {
        return false;
    }
    return groups.split(' ').includes('list');
}

/**
 * Toggle between different list types.
 */
const toggleList = (listTypeOrName, itemTypeOrName) => ({ editor, tr, state, dispatch, chain, commands, can, }) => {
    const { extensions } = editor.options;
    const listType = getNodeType(listTypeOrName, state.schema);
    const itemType = getNodeType(itemTypeOrName, state.schema);
    const { selection } = state;
    const { $from, $to } = selection;
    const range = $from.blockRange($to);
    if (!range) {
        return false;
    }
    const parentList = findParentNode(node => isList(node.type.name, extensions))(selection);
    if (range.depth >= 1 && parentList && range.depth - parentList.depth <= 1) {
        // remove list
        if (parentList.node.type === listType) {
            return commands.liftListItem(itemType);
        }
        // change list type
        if (isList(parentList.node.type.name, extensions)
            && listType.validContent(parentList.node.content)
            && dispatch) {
            tr.setNodeMarkup(parentList.pos, listType);
            return true;
        }
    }
    const canWrapInList = can().wrapInList(listType);
    // try to convert node to paragraph if needed
    if (!canWrapInList) {
        return chain()
            .clearNodes()
            .wrapInList(listType)
            .run();
    }
    return commands.wrapInList(listType);
};

var toggleList$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    toggleList: toggleList
});

/**
 * Toggle a mark on and off.
 */
const toggleMark = (typeOrName, attributes) => ({ state, commands }) => {
    const type = getMarkType(typeOrName, state.schema);
    const isActive = isMarkActive(state, type, attributes);
    if (isActive) {
        return commands.unsetMark(type);
    }
    return commands.setMark(type, attributes);
};

var toggleMark$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    toggleMark: toggleMark
});

/**
 * Toggle a node with another node.
 */
const toggleNode = (typeOrName, toggleTypeOrName, attrs = {}) => ({ state, commands }) => {
    const type = getNodeType(typeOrName, state.schema);
    const toggleType = getNodeType(toggleTypeOrName, state.schema);
    const isActive = isNodeActive(state, type, attrs);
    if (isActive) {
        return commands.setNode(toggleType);
    }
    return commands.setNode(type, attrs);
};

var toggleNode$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    toggleNode: toggleNode
});

/**
 * Wraps nodes in another node, or removes an existing wrap.
 */
const toggleWrap = (typeOrName, attributes = {}) => ({ state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    const isActive = isNodeActive(state, type, attributes);
    if (isActive) {
        return lift$2(state, dispatch);
    }
    return wrapIn$2(type, attributes)(state, dispatch);
};

var toggleWrap$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    toggleWrap: toggleWrap
});

/**
 * Undo an input rule.
 */
const undoInputRule = () => ({ state, dispatch }) => {
    return undoInputRule$2(state, dispatch);
};

var undoInputRule$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    undoInputRule: undoInputRule
});

/**
 * Remove all marks in the current selection.
 */
const unsetAllMarks = () => ({ tr, state, dispatch }) => {
    const { selection } = tr;
    const { from, to, empty } = selection;
    if (empty) {
        return true;
    }
    if (dispatch) {
        Object
            .entries(state.schema.marks)
            .forEach(([, mark]) => {
            tr.removeMark(from, to, mark);
        });
    }
    return true;
};

var unsetAllMarks$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    unsetAllMarks: unsetAllMarks
});

/**
 * Remove all marks in the current selection.
 */
const unsetMark = (typeOrName) => ({ tr, state, dispatch }) => {
    const { selection } = tr;
    const type = getMarkType(typeOrName, state.schema);
    let { from, to } = selection;
    const { $from, empty } = selection;
    if (empty) {
        const range = getMarkRange($from, type);
        if (range) {
            from = range.from;
            to = range.to;
        }
    }
    if (dispatch) {
        tr.removeMark(from, to, type);
        tr.removeStoredMark(type);
    }
    return true;
};

var unsetMark$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    unsetMark: unsetMark
});

/**
 * Update attributes of a node.
 */
const updateNodeAttributes = (typeOrName, attributes) => ({ tr, state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    const { selection } = tr;
    const { from, to } = selection;
    state.doc.nodesBetween(from, to, (node, pos) => {
        if (node.type === type && dispatch) {
            tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                ...attributes,
            });
        }
    });
    return true;
};

var updateNodeAttributes$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    updateNodeAttributes: updateNodeAttributes
});

/**
 * Wraps nodes in another node.
 */
const wrapIn = (typeOrName, attributes = {}) => ({ state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    const isActive = isNodeActive(state, type, attributes);
    if (isActive) {
        return false;
    }
    return wrapIn$2(type, attributes)(state, dispatch);
};

var wrapIn$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    wrapIn: wrapIn
});

/**
 * Wrap a node in a list.
 */
const wrapInList = (typeOrName, attrs) => ({ state, dispatch }) => {
    const type = getNodeType(typeOrName, state.schema);
    return wrapInList$2(type, attrs)(state, dispatch);
};

var wrapInList$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    wrapInList: wrapInList
});

const Commands = Extension.create({
    name: 'commands',
    addCommands() {
        return {
            ...blur$1,
            ...clearContent$1,
            ...clearNodes$1,
            ...command$1,
            ...createParagraphNear$1,
            ...deleteRange$1,
            ...deleteSelection$1,
            ...exitCode$1,
            ...extendMarkRange$1,
            ...first$1,
            ...focus$1,
            ...insertHTML$1,
            ...insertText$1,
            ...joinBackward$1,
            ...joinForward$1,
            ...lift$1,
            ...liftEmptyBlock$1,
            ...liftListItem$1,
            ...newlineInCode$1,
            ...replace$1,
            ...replaceRange$1,
            ...resetNodeAttributes$1,
            ...scrollIntoView$1,
            ...selectAll$1,
            ...selectNodeBackward$1,
            ...selectNodeForward$1,
            ...selectParentNode$1,
            ...setContent$1,
            ...setMark$1,
            ...setNode$1,
            ...sinkListItem$1,
            ...splitBlock$1,
            ...splitListItem$1,
            ...toggleList$1,
            ...toggleMark$1,
            ...toggleNode$1,
            ...toggleWrap$1,
            ...undoInputRule$1,
            ...unsetAllMarks$1,
            ...unsetMark$1,
            ...updateNodeAttributes$1,
            ...wrapIn$1,
            ...wrapInList$1,
        };
    },
});

const Editable = Extension.create({
    name: 'editable',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('editable'),
                props: {
                    editable: () => this.editor.options.editable,
                },
            }),
        ];
    },
});

const FocusEvents = Extension.create({
    name: 'focusEvents',
    addProseMirrorPlugins() {
        const { editor } = this;
        return [
            new Plugin({
                key: new PluginKey('focusEvents'),
                props: {
                    attributes: {
                        tabindex: '0',
                    },
                    handleDOMEvents: {
                        focus: (view, event) => {
                            editor.isFocused = true;
                            const transaction = editor.state.tr
                                .setMeta('focus', { event })
                                .setMeta('addToHistory', false);
                            view.dispatch(transaction);
                            return false;
                        },
                        blur: (view, event) => {
                            editor.isFocused = false;
                            const transaction = editor.state.tr
                                .setMeta('blur', { event })
                                .setMeta('addToHistory', false);
                            view.dispatch(transaction);
                            return false;
                        },
                    },
                },
            }),
        ];
    },
});

const Keymap = Extension.create({
    name: 'keymap',
    addKeyboardShortcuts() {
        const handleBackspace = () => this.editor.commands.first(({ commands }) => [
            () => commands.undoInputRule(),
            () => commands.deleteSelection(),
            () => commands.joinBackward(),
            () => commands.selectNodeBackward(),
        ]);
        const handleDelete = () => this.editor.commands.first(({ commands }) => [
            () => commands.deleteSelection(),
            () => commands.joinForward(),
            () => commands.selectNodeForward(),
        ]);
        return {
            Enter: () => this.editor.commands.first(({ commands }) => [
                () => commands.newlineInCode(),
                () => commands.createParagraphNear(),
                () => commands.liftEmptyBlock(),
                () => commands.splitBlock(),
            ]),
            'Mod-Enter': () => this.editor.commands.exitCode(),
            Backspace: () => handleBackspace(),
            'Mod-Backspace': () => handleBackspace(),
            Delete: () => handleDelete(),
            'Mod-Delete': () => handleDelete(),
        };
    },
});

var extensions = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ClipboardTextSerializer: ClipboardTextSerializer,
    Commands: Commands,
    Editable: Editable,
    FocusEvents: FocusEvents,
    Keymap: Keymap
});

const style = `.ProseMirror {
  position: relative;
}

.ProseMirror {
  word-wrap: break-word;
  white-space: pre-wrap;
  -webkit-font-variant-ligatures: none;
  font-variant-ligatures: none;
}

.ProseMirror [contenteditable="false"] {
  white-space: normal;
}

.ProseMirror [contenteditable="false"] [contenteditable="true"] {
  white-space: pre-wrap;
}

.ProseMirror pre {
  white-space: pre-wrap;
}

.ProseMirror-gapcursor {
  display: none;
  pointer-events: none;
  position: absolute;
}

.ProseMirror-gapcursor:after {
  content: "";
  display: block;
  position: absolute;
  top: -2px;
  width: 20px;
  border-top: 1px solid black;
  animation: ProseMirror-cursor-blink 1.1s steps(2, start) infinite;
}

@keyframes ProseMirror-cursor-blink {
  to {
    visibility: hidden;
  }
}

.ProseMirror-hideselection *::selection {
  background: transparent;
}

.ProseMirror-hideselection *::-moz-selection {
  background: transparent;
}

.ProseMirror-hideselection * {
  caret-color: transparent;
}

.ProseMirror-focused .ProseMirror-gapcursor {
  display: block;
}`;

let Editor = class Editor extends EventEmitter {
    constructor(options = {}) {
        super();
        this.isFocused = false;
        this.options = {
            element: document.createElement('div'),
            content: '',
            injectCSS: true,
            extensions: [],
            autofocus: false,
            editable: true,
            editorProps: {},
            parseOptions: {},
            enableInputRules: true,
            enablePasteRules: true,
            onCreate: () => null,
            onUpdate: () => null,
            onSelection: () => null,
            onTransaction: () => null,
            onFocus: () => null,
            onBlur: () => null,
            onDestroy: () => null,
        };
        /**
         * Creates a ProseMirror document.
         */
        this.createDocument = (content, parseOptions = this.options.parseOptions) => {
            if (content && typeof content === 'object') {
                try {
                    return this.schema.nodeFromJSON(content);
                }
                catch (error) {
                    console.warn('[tiptap warn]: Invalid content.', 'Passed value:', content, 'Error:', error);
                    return this.createDocument('');
                }
            }
            if (typeof content === 'string') {
                return DOMParser
                    .fromSchema(this.schema)
                    .parse(elementFromString(content), parseOptions);
            }
            return this.createDocument('');
        };
        this.options = { ...this.options, ...options };
        this.on('createdProxy', this.init);
    }
    /**
     * This method is called after the proxy is initialized.
     */
    init() {
        this.createCommandManager();
        this.createExtensionManager();
        this.createSchema();
        this.createView();
        this.injectCSS();
        this.on('create', this.options.onCreate);
        this.on('update', this.options.onUpdate);
        this.on('selection', this.options.onSelection);
        this.on('transaction', this.options.onTransaction);
        this.on('focus', this.options.onFocus);
        this.on('blur', this.options.onBlur);
        this.on('destroy', this.options.onDestroy);
        window.setTimeout(() => {
            this.commands.focus(this.options.autofocus);
            this.emit('create');
        }, 0);
    }
    /**
     * A magic method to call commands.
     *
     * @param name The name of the command
     */
    // eslint-disable-next-line
    __get(name) {
        // TODO: maybe remove proxy
    }
    /**
     * An object of all registered commands.
     */
    get commands() {
        return this.commandManager.createCommands();
    }
    /**
     * Create a command chain to call multiple commands at once.
     */
    chain() {
        return this.commandManager.createChain();
    }
    /**
     * Check if a command or a command chain can be executed. Without executing it.
     */
    can() {
        return this.commandManager.createCan();
    }
    /**
     * Inject CSS styles.
     */
    injectCSS() {
        if (this.options.injectCSS && document) {
            this.css = createStyleTag(style);
        }
    }
    /**
     * Update editor options.
     *
     * @param options A list of options
     */
    setOptions(options = {}) {
        this.options = { ...this.options, ...options };
        if (this.view && this.state && !this.isDestroyed) {
            this.view.updateState(this.state);
        }
    }
    /**
     * Returns whether the editor is editable.
     */
    get isEditable() {
        return this.view && this.view.editable;
    }
    /**
     * Returns the editor state.
     */
    get state() {
        return this.view.state;
    }
    /**
     * Register a list of commands.
     *
     * @param commands A list of commands
     */
    registerCommands(commands) {
        Object
            .entries(commands)
            .forEach(([name, command]) => this.registerCommand(name, command));
    }
    /**
     * Register a command.
     *
     * @param name The name of your command
     * @param callback The method of your command
     */
    registerCommand(name, callback) {
        this.commandManager.registerCommand(name, callback);
        return this.proxy;
    }
    /**
     * Register a ProseMirror plugin.
     *
     * @param plugin A ProseMirror plugin
     * @param handlePlugins Control how to merge the plugin into the existing plugins.
     */
    registerPlugin(plugin, handlePlugins) {
        const plugins = typeof handlePlugins === 'function'
            ? handlePlugins(plugin, this.state.plugins)
            : [...this.state.plugins, plugin];
        const state = this.state.reconfigure({ plugins });
        this.view.updateState(state);
    }
    /**
     * Unregister a ProseMirror plugin.
     *
     * @param name The plugins name
     */
    unregisterPlugin(name) {
        const state = this.state.reconfigure({
            // @ts-ignore
            plugins: this.state.plugins.filter(plugin => !plugin.key.startsWith(`${name}$`)),
        });
        this.view.updateState(state);
    }
    /**
     * Creates an extension manager.
     */
    createExtensionManager() {
        const coreExtensions = Object.entries(extensions).map(([, extension]) => extension);
        const allExtensions = [...coreExtensions, ...this.options.extensions].filter(extension => {
            return ['extension', 'node', 'mark'].includes(extension === null || extension === void 0 ? void 0 : extension.type);
        });
        this.extensionManager = new ExtensionManager(allExtensions, this.proxy);
    }
    /**
     * Creates an command manager.
     */
    createCommandManager() {
        this.commandManager = new CommandManager(this.proxy);
    }
    /**
     * Creates a ProseMirror schema.
     */
    createSchema() {
        this.schema = this.extensionManager.schema;
    }
    /**
     * Creates a ProseMirror view.
     */
    createView() {
        this.view = new EditorView(this.options.element, {
            ...this.options.editorProps,
            dispatchTransaction: this.dispatchTransaction.bind(this),
            state: EditorState.create({
                doc: this.createDocument(this.options.content),
            }),
        });
        // `editor.view` is not yet available at this time.
        // Therefore we will add all plugins and node views directly afterwards.
        const newState = this.state.reconfigure({
            plugins: this.extensionManager.plugins,
        });
        this.view.updateState(newState);
        this.createNodeViews();
        // Let’s store the editor instance in the DOM element.
        // So we’ll have access to it for tests.
        const dom = this.view.dom;
        dom.editor = this.proxy;
    }
    /**
     * Creates all node views.
     */
    createNodeViews() {
        this.view.setProps({
            nodeViews: this.extensionManager.nodeViews,
        });
    }
    /**
     * The callback over which to send transactions (state updates) produced by the view.
     *
     * @param transaction An editor state transaction
     */
    dispatchTransaction(transaction) {
        const state = this.state.apply(transaction);
        const selectionHasChanged = !this.state.selection.eq(state.selection);
        this.view.updateState(state);
        this.emit('transaction', { transaction });
        if (selectionHasChanged) {
            this.emit('selection');
        }
        const focus = transaction.getMeta('focus');
        const blur = transaction.getMeta('blur');
        if (focus) {
            this.emit('focus', { event: focus.event });
        }
        if (blur) {
            this.emit('blur', { event: blur.event });
        }
        if (!transaction.docChanged || transaction.getMeta('preventUpdate')) {
            return;
        }
        this.emit('update', { transaction });
    }
    /**
     * Get attributes of the currently selected node.
     *
     * @param name Name of the node
     */
    getNodeAttributes(name) {
        return getNodeAttributes(this.state, name);
    }
    /**
     * Get attributes of the currently selected mark.
     *
     * @param name Name of the mark
     */
    getMarkAttributes(name) {
        return getMarkAttributes(this.state, name);
    }
    isActive(nameOrAttributes, attributesOrUndefined) {
        const name = typeof nameOrAttributes === 'string'
            ? nameOrAttributes
            : null;
        const attributes = typeof nameOrAttributes === 'string'
            ? attributesOrUndefined
            : nameOrAttributes;
        return isActive(this.state, name, attributes);
    }
    /**
     * Get the document as JSON.
     */
    getJSON() {
        return this.state.doc.toJSON();
    }
    /**
     * Get the document as HTML.
     */
    getHTML() {
        return getHTMLFromFragment(this.state.doc, this.schema);
    }
    /**
     * Check if there is no content.
     */
    isEmpty() {
        var _a;
        const defaultContent = (_a = this.state.doc.type.createAndFill()) === null || _a === void 0 ? void 0 : _a.toJSON();
        const content = this.getJSON();
        return JSON.stringify(defaultContent) === JSON.stringify(content);
    }
    /**
     * Get the number of characters for the current document.
     */
    getCharacterCount() {
        return this.state.doc.content.size - 2;
    }
    /**
     * Destroy the editor.
     */
    destroy() {
        this.emit('destroy');
        if (this.view) {
            this.view.destroy();
        }
        this.removeAllListeners();
        removeElement(this.css);
    }
    /**
     * Check if the editor is already destroyed.
     */
    get isDestroyed() {
        var _a;
        // @ts-ignore
        return !((_a = this.view) === null || _a === void 0 ? void 0 : _a.docView);
    }
};
Editor = __decorate([
    magicMethods
], Editor);

var _configure$1;
class Node {
    constructor(config) {
        this.type = 'node';
        this.config = {
            name: 'node',
            defaultOptions: {},
            addGlobalAttributes: () => [],
            addCommands: () => ({}),
            addKeyboardShortcuts: () => ({}),
            addInputRules: () => [],
            addPasteRules: () => [],
            addProseMirrorPlugins: () => [],
            topNode: false,
            content: null,
            marks: null,
            group: null,
            inline: null,
            atom: null,
            selectable: null,
            draggable: null,
            code: null,
            defining: null,
            isolating: null,
            parseHTML: () => null,
            renderHTML: null,
            renderText: null,
            addAttributes: () => ({}),
            addNodeView: null,
            onCreate: null,
            onUpdate: null,
            onSelection: null,
            onTransaction: null,
            onFocus: null,
            onBlur: null,
            onDestroy: null,
        };
        _configure$1.set(this, (options) => {
            this.options = mergeDeep(this.config.defaultOptions, options);
            return this;
        });
        this.config = {
            ...this.config,
            ...config,
        };
        this.options = this.config.defaultOptions;
    }
    static create(config) {
        return new Node(config);
    }
    configure(options = {}) {
        var _a;
        return __classPrivateFieldGet((_a = Node
            .create(this.config)), _configure$1).call(_a, options);
    }
    extend(extendedConfig) {
        return new Node({
            ...this.config,
            ...extendedConfig,
        });
    }
}
_configure$1 = new WeakMap();

var _configure$2;
class Mark {
    constructor(config) {
        this.type = 'mark';
        this.config = {
            name: 'mark',
            defaultOptions: {},
            addGlobalAttributes: () => [],
            addCommands: () => ({}),
            addKeyboardShortcuts: () => ({}),
            addInputRules: () => [],
            addPasteRules: () => [],
            addProseMirrorPlugins: () => [],
            inclusive: null,
            excludes: null,
            group: null,
            spanning: null,
            parseHTML: () => null,
            renderHTML: null,
            addAttributes: () => ({}),
            onCreate: null,
            onUpdate: null,
            onSelection: null,
            onTransaction: null,
            onFocus: null,
            onBlur: null,
            onDestroy: null,
        };
        _configure$2.set(this, (options) => {
            this.options = mergeDeep(this.config.defaultOptions, options);
            return this;
        });
        this.config = {
            ...this.config,
            ...config,
        };
        this.options = this.config.defaultOptions;
    }
    static create(config) {
        return new Mark(config);
    }
    configure(options = {}) {
        var _a;
        return __classPrivateFieldGet((_a = Mark
            .create(this.config)), _configure$2).call(_a, options);
    }
    extend(extendedConfig) {
        return new Mark({
            ...this.config,
            ...extendedConfig,
        });
    }
}
_configure$2 = new WeakMap();

function nodeInputRule (regexp, type, getAttributes) {
    return new InputRule(regexp, (state, match, start, end) => {
        const attributes = getAttributes instanceof Function
            ? getAttributes(match)
            : getAttributes;
        const { tr } = state;
        if (match[0]) {
            tr.replaceWith(start - 1, end, type.create(attributes));
        }
        return tr;
    });
}

function getMarksBetween(from, to, state) {
    let marks = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
        marks = [...marks, ...node.marks.map(mark => ({
                from: pos,
                to: pos + node.nodeSize,
                mark,
            }))];
    });
    return marks;
}

function markInputRule (regexp, markType, getAttributes) {
    return new InputRule(regexp, (state, match, start, end) => {
        const attributes = getAttributes instanceof Function
            ? getAttributes(match)
            : getAttributes;
        const { tr } = state;
        const captureGroup = match[match.length - 1];
        const fullMatch = match[0];
        let markEnd = end;
        if (captureGroup) {
            const startSpaces = fullMatch.search(/\S/);
            const textStart = start + fullMatch.indexOf(captureGroup);
            const textEnd = textStart + captureGroup.length;
            const excludedMarks = getMarksBetween(start, end, state)
                .filter(item => {
                // TODO: PR to add excluded to MarkType
                // @ts-ignore
                const { excluded } = item.mark.type;
                return excluded.find((type) => type.name === markType.name);
            })
                .filter(item => item.to > textStart);
            if (excludedMarks.length) {
                return null;
            }
            if (textEnd < end) {
                tr.delete(textEnd, end);
            }
            if (textStart > start) {
                tr.delete(start + startSpaces, textStart);
            }
            markEnd = start + startSpaces + captureGroup.length;
            tr.addMark(start + startSpaces, markEnd, markType.create(attributes));
            tr.removeStoredMark(markType);
        }
        return tr;
    });
}

function markPasteRule (regexp, type, getAttrs) {
    const handler = (fragment, parent) => {
        const nodes = [];
        fragment.forEach(child => {
            if (child.isText && child.text) {
                const { text } = child;
                let pos = 0;
                let match;
                // eslint-disable-next-line
                while ((match = regexp.exec(text)) !== null) {
                    const outerMatch = Math.max(match.length - 2, 0);
                    const innerMatch = Math.max(match.length - 1, 0);
                    if (parent.type.allowsMarkType(type)) {
                        const start = match.index;
                        const matchStart = start + match[0].indexOf(match[outerMatch]);
                        const matchEnd = matchStart + match[outerMatch].length;
                        const textStart = matchStart + match[outerMatch].lastIndexOf(match[innerMatch]);
                        const textEnd = textStart + match[innerMatch].length;
                        const attrs = getAttrs instanceof Function ? getAttrs(match) : getAttrs;
                        // adding text before markdown to nodes
                        if (matchStart > 0) {
                            nodes.push(child.cut(pos, matchStart));
                        }
                        // adding the markdown part to nodes
                        nodes.push(child
                            .cut(textStart, textEnd)
                            .mark(type.create(attrs).addToSet(child.marks)));
                        pos = matchEnd;
                    }
                }
                // adding rest of text to nodes
                if (pos < text.length) {
                    nodes.push(child.cut(pos));
                }
            }
            else {
                nodes.push(child.copy(handler(child.content, child)));
            }
        });
        return Fragment.fromArray(nodes);
    };
    return new Plugin({
        key: new PluginKey('markPasteRule'),
        props: {
            transformPasted: slice => {
                return new Slice(handler(slice.content), slice.openStart, slice.openEnd);
            },
        },
    });
}

function generateHTML(doc, extensions) {
    const schema = getSchema(extensions);
    const contentNode = Node$1.fromJSON(schema, doc);
    return getHTMLFromFragment(contentNode, schema);
}

function isNodeSelection(value) {
    return isObject(value) && value instanceof NodeSelection;
}

function isCellSelection(value) {
    return isObject(value) && value instanceof CellSelection;
}

export { Editor, Extension, Mark, Node, extensions, findParentNodeClosestToPos, generateHTML, getHTMLFromFragment, getMarkAttributes, getSchema, isActive, isCellSelection, isMarkActive, isNodeActive, isNodeSelection, isTextSelection, markInputRule, markPasteRule, mergeAttributes, nodeInputRule };
//# sourceMappingURL=tiptap-core.esm.js.map
